import React from "react"
import PropTypes from "prop-types"
import { Route, Redirect } from "react-router-dom"
import { getAppropriateLoadingGif, getBackgroundColorClass, isAdminAuthorized, isNotAuthorized, URLS_FOR_ADMIN_PERMISSIONS } from "helpers/sf_utils";
import { AppConfiguration } from "config";
import { bsh_isAdminUserDetailsPresent } from "helpers/sf_browser_storage_helper";
import { useEffect } from "react";
import { changeBackgroundColor } from "store/actions";
import { useDispatch } from "react-redux";
import { postAdminUserLogin } from "store/admin/user-login/actions";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import useFeatureFlagsForAccounts from "hooks/useFeatureFlagsForAccounts";
import { REQUIRED_FEATURE_FLAGS } from "helpers/sf_constants";
import { getIntegrationPermissions, isIntegrationRouteForbidden } from "helpers/sf_integration_permissions_helper";

// Override the redirection when the admin doesn't have required permissions (but is logged in)
const AdminOverridenRedirect = ({ pathname, adminRolePermissions, defaultRedirectionPathname, defaultRedirectionState }) => {
  let redirectionPathname = defaultRedirectionPathname;
  let redirectionState = defaultRedirectionState;
  switch (pathname.split("/")[1]) {
    case URLS_FOR_ADMIN_PERMISSIONS.ADMIN_DASHBOARD:
      // fallback check for client users permission
      const alternativePathnames = [
        `/${URLS_FOR_ADMIN_PERMISSIONS.CLIENT_USERS}`,
        `/${URLS_FOR_ADMIN_PERMISSIONS.ADMIN_PROFILE}`
      ];

      for (const alternativePathname of alternativePathnames) {
        if (isAdminAuthorized(alternativePathname, adminRolePermissions)) {
          redirectionPathname = alternativePathname;
          break;
        }
      }
  }

  return (
    <Redirect to={{ pathname: redirectionPathname, state: redirectionState }} />
  )
}

const Authmiddleware = ({
  component: Component,
  layout: Layout,
  isAuthProtected,
  loading,
  rolePermissions,
  adminRolePermissions,
  currentAccount,
  isPlatformSettingFeatures,
  ...rest
}) => {

  const parts = window.location.hostname.split('.').filter(part => part !== 'www');
  const dispatch = useDispatch();
  const history = useHistory();
  const [isFeatureFlagActiveForAccounts] = useFeatureFlagsForAccounts();
  let last_index = -2;
  const last = parts[parts.length - 1];
  const is_localhost = last === 'localhost';
  if (is_localhost) {
    last_index = -1;
  }

  const isSubdomain = !!parts.find((part) => part.includes(AppConfiguration.REACT_ADMIN_HOST_SUBDOMAIN));

  useEffect(() => {
    const url = window.location.pathname
    if (url.includes('/login') ||
      // url.includes('/logout') || Not needed because Logout component only redirects to login
      url.includes('/register') ||
      url.includes('/reset') ||
      url.includes('/forgot-password') ||
      url.includes('/admin-forgot-password') ||
      url.includes('/admin_signup') ||
      url.includes('/admin_password/edit') ||
      url.includes('/password/edit') ||
      url.includes('/password/new')
    ) {
      dispatch(changeBackgroundColor(getBackgroundColorClass()))
      document.body.style.backgroundColor = getBackgroundColorClass()

    } else {
      dispatch(changeBackgroundColor('#f5f5f6'))
      document.body.style.backgroundColor = '#f5f5f6'

    }
  },
    [Component],
  );

  //get the admin url which we are using to redirect admin user to admin login page if he's not logged in.
  const hostParts = window.location.host.split('.');
  const newHost = hostParts.map(part => {
    if (part.includes(AppConfiguration.REACT_CLIENT_HOST_SUBDOMAIN)) {
      return AppConfiguration.REACT_ADMIN_HOST_SUBDOMAIN;
    }
    return part;
  }).join('.')

  return (
    <Route
      {...rest}
      render={props => {

        let filterUrl = ''

        const p_location = props?.location;
        const p_loc_state = p_location?.state
        const p_loc_state_from = p_loc_state?.from
        const p_loc_search = p_location?.search
        const p_loc_pathname = p_location?.pathname

        //if url contains filters then get the filters part - ex: /campaigns?admin_user=true&account_id=217&filters=%255...
        if (p_loc_state && p_loc_state_from?.search.includes('filters')) {
          filterUrl = p_loc_state_from?.pathname + p_loc_state_from?.search
        }
        else if (p_loc_search.includes('filters')) {
          filterUrl = p_loc_pathname + p_loc_search
        }

        if (isAuthProtected && !localStorage.getItem("authUser") && !isSubdomain) {
          if (p_loc_search.includes('filters') && p_loc_search.includes('admin_user=true')) {
            //if admin is not logged in and url is of client side then redirect the admin user to admin login page with the url filters attached in the url
            window.location = `http://${newHost}/login?${filterUrl}`
          } else {
            return (
              <Redirect
                to={{ pathname: "/login", state: { from: props.location } }}
              />
            )
          }
        }
        if (!isAuthProtected && localStorage.getItem("authUser") && !isSubdomain && p_loc_pathname !== '/logout') {
          const url_filters_endpoint = p_loc_search.split('url_filters=')[1];
          if (url_filters_endpoint?.includes('filters') && url_filters_endpoint?.includes('admin_user=true')) {
            history.push(url_filters_endpoint)
            return;
          } else {
            return (
              <Redirect to={{ pathname: "/", state: { from: props.location } }} />
            )
          }
        }

        if (isAuthProtected && !bsh_isAdminUserDetailsPresent() && isSubdomain) {
          if (p_loc_search.includes('filters') && p_loc_search.includes('admin_user=true')) {
            //if admin is not logged in and url is of admin side then redirect the admin user to admin login page with the url filters attached in the url
            window.location = `http://${newHost}/login?${filterUrl}`
          } else {
            return (
              <Redirect
                to={{ pathname: "/login", state: { from: props.location } }}
              />
            )
          }
        }
        if (!isAuthProtected && bsh_isAdminUserDetailsPresent() && isSubdomain) {
          //props?.location?.search starts with '?' and then the filterUrl so removing '?'
          const url_filters_endpoint = p_loc_search.substring(1)
          const urlParams = new URLSearchParams(url_filters_endpoint);
          let accountIdFromUrl = urlParams.get('account_id');
          //checking if accountIdFromUrl is there to confirm if it's client side url
          if (url_filters_endpoint?.includes('filters') && url_filters_endpoint?.includes('admin_user=true') && accountIdFromUrl) {
            //if admin is logged in already and then he enters the filter url then first login into that client with account id and then redirect him to that page.
            !currentAccount && dispatch(postAdminUserLogin({ user: { account: accountIdFromUrl }, history: history }, url_filters_endpoint))
          }
          return (
            <Redirect to={{ pathname: "/", state: { from: props.location } }} />
          )
        }

        if (isAuthProtected && localStorage.getItem("authUser") && rolePermissions) {
          if (isNotAuthorized(p_location, rolePermissions, currentAccount, isPlatformSettingFeatures)) {
            return (
              <Redirect to={{ pathname: "/forbidden", state: { from: p_location } }} />
            )
          }

          // BM-9524: /integrations/* by integration permission (flag on only)
          const integrationPermissions = getIntegrationPermissions({
            flagOn: isFeatureFlagActiveForAccounts(REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_INTEGRATION_ROLE_PERMISSIONS, currentAccount),
            isAdmin: bsh_isAdminUserDetailsPresent(),
            rolePermissions,
          });
          if (isIntegrationRouteForbidden(p_loc_pathname, integrationPermissions)) {
            return (
              <Redirect to={{ pathname: "/forbidden", state: { from: p_location } }} />
            )
          }
        }

        if (isAuthProtected && bsh_isAdminUserDetailsPresent() && isSubdomain && adminRolePermissions) {
          if (!isAdminAuthorized(p_loc_pathname, adminRolePermissions)) {
            return (
              <AdminOverridenRedirect
                pathname={p_loc_pathname}
                adminRolePermissions={adminRolePermissions}
                defaultRedirectionPathname='/forbidden'
                defaultRedirectionState={{ from: p_location }}
              />
            )
          }
        }

        return (
          <Layout>
            {loading ? <>{getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}</> :
              <Component {...props} />}
          </Layout>
        )
      }}
    />
  )
}

Authmiddleware.propTypes = {
  isAuthProtected: PropTypes.bool,
  component: PropTypes.any,
  location: PropTypes.object,
  layout: PropTypes.any,
}

export default Authmiddleware
